import { insensitive, onlyDefined } from '@acme/common'
import { helpers } from '@acme/core'
import { z } from 'zod'

const enderecoSchema = z.object({
    Address: z.object({
        street: z.string().min(1, 'Obrigatório'),
        number: z.string().min(1, 'Obrigatório'),
        neighborhood: z
            .string()
            .nullish()
            .transform(value => {
                if (!value) return 'N/A'
                return value
            }),
        complement: z
            .string()
            .nullish()
            .transform(value => {
                if (!value) return 'N/A'
                return value
            }),
        city: z.string().min(1, 'Obrigatório'),
        state: z.string().min(1, 'Obrigatório'),
        zipCode: z
            .string()
            .min(1, 'Obrigatório')
            .transform(value => value.replace(/[^0-9]/g, '')),
    }),
})

const CustomerSchema = z.object({
    name: z.string().min(3).max(240),
    document: z
        .string()
        .refine(
            v => helpers.isDocumentoEmpresaValido(v),
            'O documento é inválido',
        )
        .transform(v => v.replace(/[^0-9]/g, '')),
    emails: z
        .object({
            principal: z.coerce.string().email().trim(),
            secundario: z.coerce.string().email().trim().optional(),
        })
        .transform(value => {
            return Object.values(value).filter(onlyDefined)
        }),
    phones: z
        .object({
            principal: z.coerce.string().min(10).max(15),
            secundario: z.coerce.string().min(10).max(15).optional(),
        })
        .transform(value => {
            return Object.values(value)
                .map(v => v?.toString().replace(/[^0-9]/g, ''))
                .filter(Boolean)
                .map(Number)
        }),
    Address: enderecoSchema.shape.Address,
})

const pagamentoSchema = z
    .object({
        mainPaymentMethodId: z.string().min(1, 'Obrigatório'),
        Card: z
            .object({
                number: z.string().min(19, 'Número do cartão inválido'),
                holder: z
                    .string()
                    .trim()
                    .min(2, 'Nome do titular inválido')
                    .transform(v => insensitive(v)),
                expiresAt: z
                    .string()
                    .regex(
                        /^\d{2}\/(\d{2}|\d{4})$/,
                        'Data inválida, utilize MM/YY ou MM/YYYY',
                    )
                    .transform(value => {
                        const [month, year] = value.split('/')
                        const fullYear =
                            year.length === 2
                                ? `${new Date().getFullYear().toString().slice(0, 2)}${year}`
                                : year
                        return `${fullYear}-${month}`
                    }),
                cvv: z
                    .string()
                    .refine(
                        v => String(v).replace(/[^0-9]/g, '').length === 3,
                        {
                            message: 'CVV inválido',
                        },
                    ),
            })
            .optional(),
    })
    .superRefine((data, ctx) => {
        if (data.mainPaymentMethodId === 'creditcard' && !data.Card) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Você deve informar os dados do cartão',
                path: ['Card'],
            })
        }
    })

const PlanoSchema = z.object({
    periodicity: z.enum(['monthly', 'yearly', 'quarterly'], {
        errorMap: () => ({ message: 'Obrigatório' }),
    }),
    limiteFuncionarios: z.number(),
    price: z.number().min(1, 'Obrigatório'),
})

export const checkoutSchemas = {
    CustomerCreateSchema: z.object({ Customer: CustomerSchema }),
    CustomerMyIdSchema: z.object({
        Customer: z.object({
            myId: z.string().min(1, 'Termine o cadastro'),
        }),
    }),
    pagamento: pagamentoSchema,
    root: z
        .object({
            Customer: z.object({
                myId: z.string().min(1, 'Termine o cadastro'),
            }),
            Card: z
                .object({
                    myId: z.string().optional(),
                })
                .optional(),
            mainPaymentMethodId: z.enum(['boleto', 'creditcard']),
            termos: z.boolean().refine(value => !!value, {
                message: 'Você deve aceitar os termos de uso',
            }),
            privacidade: z.boolean().refine(value => !!value, {
                message: 'Você deve aceitar a política de privacidade',
            }),
        })
        .superRefine((data, ctx) => {
            if (data.mainPaymentMethodId === 'creditcard' && !data.Card?.myId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Termine o cadastro do cartão',
                    path: ['Card'],
                })
            }
        }),
    plano: PlanoSchema,
}

export type CreateSubscriptionBody = z.infer<typeof checkoutSchemas.root> &
    Omit<z.infer<typeof PlanoSchema>, 'price'>
